import type { CampaignDetails } from '~/campaign/types';
import type { LinkedInEntityDetails } from '~/editor/plugins/linkedin-annotations/nodes/LinkedInAnnotationElement';
import { UserTag } from '~/user/types';

export type RpcUpdateStatistics = {
  retweets?: number;
  repins?: number;
  favorites?: number;
  mentions?: number;
  clicks?: number;
  reach?: number;
};

export type RpcVideoDetails = {
  duration: number;
  duration_millis: number;
  file_size: number;
  width: number;
  height: number;
  transcoded_location: string;
  location: string;
};

export type RpcMediaVideo = {
  id: string;
  details: RpcVideoDetails;
  thumbnails: string[];
  title: string;
};

export type RpcUpdateMedia = {
  progress: string;
  uploaded: string;
  thumbnail: string;
  alt_text?: string;
  source?: string;
  title?: string;
  picture?: string;
  uploading_type?: string;
  video?: RpcMediaVideo;
  photo?: string;
};

export type RpcPostContent = {
  imageSrc?: string;
  imageUrls?: string[];
  linkAttachment?: { title: string };
  links?: string[];
  retweetCommentLinks?: string[];
  text: string;
  type: string;
};

export type RpcPostDetails = {
  postAction: string;
  isRetweet: boolean;
  error?: unknown;
  errorLink?: string;
  isCustomScheduled: boolean;
  isInstagramReminder: boolean;
  isPendingTiktokPost: boolean;
};

export type RpcUpdateUser = {
  email: string;
  name?: string;
  gravatar?: string;
  avatar: string;
  id: string;
};

export type LinkedInAnnotation = LinkedInEntityDetails & {
  start: number;
  length: number;
};

export enum CommentType {
  Comment = 'COMMENT',
  History = 'HISTORY',
}

export type PostComment = {
  id: string;
  createdAt: number;
  text: string;
  type: CommentType;
  author: UserTag;
};

export type RpcUpdate = {
  day: string;
  id: string;
  updateType?: string;
  createdAt: number;
  profileId: string;
  isPastDue: boolean;
  imageSrc?: string;
  imageUrls: string[] | [];
  scheduled_at?: number;
  scheduledAt?: number;
  links: string[] | [];
  profileTimezone: string;
  linkAttachment: { title?: string };
  needsApproval: boolean;
  postDetails: RpcPostDetails;
  profile_service: string;
  retweetCommentLinks?: [];
  isSent: boolean;
  isPastReminder: boolean;
  isDraft: boolean;
  title?: string;
  source_url?: string;
  commentText?: string;
  text: string;
  type: string;
  media?: RpcUpdateMedia;
  extra_media?: RpcUpdateMedia[];
  subprofile_id?: string;
  due_at: number;
  pinned: boolean;
  isFixed: boolean;
  statistics: RpcUpdateStatistics;
  user: RpcUpdateUser;
  serviceLink?: string;
  serviceUpdateId?: string;
  dueTime: string;
  commentEnabled: boolean;
  campaignDetails?: CampaignDetails;
  postContent: RpcPostContent;
  hasPushNotifications?: boolean;
  profileService?: string;
  profileServiceType?: string;
  isManager: boolean;
  dueAt?: number;
  retweet?: boolean;
  shopgridLink?: string;
  annotations?: LinkedInAnnotation[];
  comments?: PostComment[];
  headerDetails?: {
    channel?: {
      avatarUrl: string;
      handle: string;
      type: string;
    };
    hideCreatorDetails?: boolean;
  };
  ideaId?: string;
  fromIdea?: boolean;
  thread?: {
    text: string;
  }[];
};

export enum PostActionType {
  RESCHEDULE = 'RESCHEDULE',
  ADD_TO_QUEUE = 'ADD_TO_QUEUE',
  SCHEDULE_POST = 'SCHEDULE_POST',
  REQUEST_APPROVAL = 'REQUEST_APPROVAL',
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  REVERT_TO_DRAFT = 'REVERT_TO_DRAFT',
  MOVE_POST_TO_DRAFTS = 'MOVE_POST_TO_DRAFTS',
  SHARE_NOW = 'SHARE_NOW',
}

export enum SentPostActionType {
  SHARE_LINK = 'SHARE_LINK',
  SHARE_AGAIN = 'SHARE_AGAIN',
  VIEW_POST = 'VIEW_POST',
}

export enum PostActionButtonType {
  // Post action prefers to be rendered separately
  SINGLE = 'SINGLE',
  // Post action prefers to be rendered nested along with other post actions
  STACKED = 'STACKED',
}

export enum PostActionLabel {
  RESCHEDULE = 'Reschedule',
  ADD_TO_QUEUE = 'Add to Queue',
  SCHEDULE_POST = 'Schedule Post',
  REQUEST_APPROVAL = 'Request Approval',
  APPROVE = 'Approve',
  REJECT = 'Reject',
  REVERT_TO_DRAFT = 'Revert to Draft',
  MOVE_POST_TO_DRAFTS = 'Move to Drafts',
  SHARE_NOW = 'Share Now',
}

export enum SentPostActionLabel {
  SHARE_LINK = 'Share Link',
  SHARE_AGAIN = 'Share Again',
  VIEW_POST = 'View Post',
}

export enum PostActionTooltipMessage {
  RESCHEDULE = '',
  ADD_TO_QUEUE = 'This draft will be added to the next available time slot in the queue.',
  SCHEDULE_POST = 'This draft will be added to the queue for the selected date and time.',
  REQUEST_APPROVAL = '',
  APPROVE = '',
  REJECT = 'This post will be moved back to drafts.',
  REVERT_TO_DRAFT = '',
  MOVE_POST_TO_DRAFTS = 'This post will be turned into a draft.',
  SHARE_NOW = 'This post will be shared to your channel immediately',
}

export enum PostActionNotificationSuccess {
  RESCHEDULE = 'Great! Your draft has been rescheduled.',
  ADD_TO_QUEUE = 'Done! Your post has been added to the queue.',
  SCHEDULE_POST = 'Done! Your draft has been scheduled as a post.',
  REJECT = "We've sent that post back to drafts.",
  REQUEST_APPROVAL = 'Done! Your draft has been sent for approval.',
  REVERT_TO_DRAFT = 'Done! Your draft is no longer pending approval',
  MOVE_POST_TO_DRAFTS = "Great! We've moved that post to drafts",
  ASSIGN_CAMPAIGN_TO_POST = 'Done! Your post has been added to the campaign. Your report was updated.',
  REMOVE_CAMPAIGN_FROM_POST = 'Done! Your post has been removed from the campaign. Your report was updated.',
  SHARE_NOW = 'Done! Your post has been shared.',
}

export enum PostActionNotificationError {
  RESCHEDULE = 'Whoops, we had some trouble rescheduling your draft.',
  ADD_TO_QUEUE = 'Whoops, we had some trouble adding your post to the queue.',
  SCHEDULE_POST = 'Whoops, we had some trouble scheduling your post.',
  REJECT = 'Whoops, we had some trouble rejecting that draft.',
  REQUEST_APPROVAL = 'Whoops, we had some trouble sending your draft for approval.',
  REVERT_TO_DRAFT = 'Whoops, we had some trouble reverting approval on your draft.',
  MOVE_POST_TO_DRAFTS = 'Whoops, we had some trouble moving that post to drafts.',
  ASSIGN_CAMPAIGN_TO_POST = 'Error! we had some trouble assigning the campaign to your post',
  REMOVE_CAMPAIGN_FROM_POST = 'Error! we had some trouble removing the campaign from your post',
  SHARE_NOW = 'Error! we had some trouble sharing your post',
}

export * from './hooks/types';
